import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import SignHealthDocumentModal from '../../../../components/signHealthDocumentModal/signHealthDocumentModal';
import { useSignedDocumentActions } from '../../../../hooks/useSignedDocumentActions';
import { useSignAttested } from '../hooks/useSignAttested';
import { useSignedAttestedDocument } from '../hooks/useSignedAttestedDocument';
import AttestedModalRouter from '../attestedModal/attestedModalRouter';
import Loading from '../../../../components/loading';
import SuccessMessage from '../../../../components/successMessage';
import ErrorMessage from '../../../../components/errorMessage';
import { useAttesteds } from '../hooks/useAttesteds';
import {
  formatAttestedType,
  formatCid,
  formatDaysOfRest,
  formatPeriod,
  formatText,
} from '../attestedFields';

export default function AttestedListWeb() {
  const { patientId } = useParams();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [isModalOpen, setModalOpen] = useState(false);

  const {
    attesteds,
    isLoading,
    error: listError,
    refetch: getAttestedsFromPatient,
  } = useAttesteds(patientId);

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const saveAttested = (returnFromApi) => {
    if (returnFromApi.data) {
      setShowSuccessMessage(true);
      setSuccessMessage(returnFromApi.data);
    } else {
      setShowErrorMessage(true);
      setErrorMessage(returnFromApi.response?.data);
    }
    setModalOpen(false);
    getAttestedsFromPatient();
  };

  const { sign: signAttested } = useSignAttested();
  const { fetchUrl: fetchSignedAttestedUrl } = useSignedAttestedDocument();
  const {
    documentToSign: attestedToSign,
    openSignModal,
    closeSignModal,
    onSigned,
    openSignedDocument,
    isOpeningSignedDocument,
  } = useSignedDocumentActions({
    fetchSignedDocumentUrl: (attestedId) => fetchSignedAttestedUrl(patientId, attestedId),
    refetch: getAttestedsFromPatient,
    onSuccess: (message) => {
      setShowSuccessMessage(true);
      setSuccessMessage(message);
    },
    onError: (message) => {
      setShowErrorMessage(true);
      setErrorMessage(message);
    },
  });

  const printAttested = (attestedId) => {
    window.open(`/attested-to-print/${patientId}/${attestedId}`, '_blank');
  };

  return (
    <>
      <Loading isToHidden={isLoading === false && isOpeningSignedDocument === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Atestados
          </Typography>
          <Button variant="outlined" onClick={() => setModalOpen(true)}>
            Adicionar Atestado
          </Button>
        </Box>

        {attesteds.length === 0 ? (
          <Typography align="center" sx={{ py: 4 }}>
            Nenhum atestado cadastrado ainda.
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Profissional</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Dias de repouso</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Período</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>CID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Assinatura digital</TableCell>
                  <TableCell sx={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attesteds.map((attested) => (
                  <TableRow key={attested.id} hover>
                    <TableCell>{formatText(attested.data)}</TableCell>
                    <TableCell>{formatAttestedType(attested)}</TableCell>
                    <TableCell>{formatText(attested.nomeDoDentista)}</TableCell>
                    <TableCell>{formatDaysOfRest(attested)}</TableCell>
                    <TableCell>{formatPeriod(attested)}</TableCell>
                    <TableCell>{formatCid(attested)}</TableCell>
                    <TableCell>
                      {attested.signed ? (
                        <Tooltip title={`${attested.signerName} · ${attested.signedAt}`}>
                          <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            icon={<VerifiedUserOutlinedIcon />}
                            label="Assinado"
                          />
                        </Tooltip>
                      ) : (
                        <Chip size="small" variant="outlined" label="Não assinado" />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {attested.signed ? (
                        <Tooltip title="Abrir PDF assinado">
                          <IconButton
                            color="primary"
                            aria-label="abrir PDF assinado"
                            onClick={() => openSignedDocument(attested.id)}
                          >
                            <PictureAsPdfOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Assinar digitalmente">
                          <IconButton
                            color="primary"
                            aria-label="assinar digitalmente"
                            onClick={() => openSignModal(attested)}
                          >
                            <DrawOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Imprimir">
                        <IconButton
                          color="primary"
                          aria-label="imprimir"
                          onClick={() => printAttested(attested.id)}
                        >
                          <PrintIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <SignHealthDocumentModal
        open={!!attestedToSign}
        onClose={closeSignModal}
        onSigned={onSigned}
        onSign={(certificatePassword, cidAuthorizedByPatient) =>
          signAttested(patientId, attestedToSign.id, {
            certificatePassword,
            cidAuthorizedByPatient,
          })
        }
        title="Assinar atestado digitalmente"
        summary={
          attestedToSign
            ? `${formatAttestedType(attestedToSign)} de ${formatText(attestedToSign.data)} · ${formatText(attestedToSign.nomeDoDentista)}`
            : null
        }
        confirmationLabel={
          attestedToSign?.cid
            ? `O(a) paciente autorizou que o diagnóstico (CID ${attestedToSign.cid}) conste neste atestado.`
            : null
        }
        confirmationRequiredMessage="Confirme que o(a) paciente autorizou a inclusão do CID no atestado."
      />

      <AttestedModalRouter
        open={isModalOpen}
        onClose={() => setModalOpen(false)}
        onAttestedSave={saveAttested}
      />
    </>
  );
}
